import type { PlmExamInput, PlmFiberTypeValue } from './plm.api';

/**
 * Optical-property autofill and the new-row balance — the two pure rules the
 * PLM Exam tab runs client-side.  Kept out of the component so they can be
 * unit-tested against the legacy behaviour they port.
 */

/** Empty exam row — every boolean false, every string null. */
export function blankExam(sort: number): PlmExamInput {
  return {
    examOid: null, sort,
    fiberTypeOid: null, fiberColorOid: null, name: null, result: null,
    typeLow: false, typeMedium: false, typeHigh: false, typeIsotropic: false,
    pleochroismYes: false, pleochroismNo: false, pleochroismColor: null,
    morphologyBundles: false, morphologyCurved: false, morphologyRibbon: false,
    morphologySplayed: false, morphologyStraight: false, morphologyWavey: false,
    morphologyUniform: false, morphologyText: null,
    elongationPositive: false, elongationNegative: false, elongationBoth: false,
    extinctionOblique: false, extinctionParallel: false, extinctionUndulose: false,
    extinctionIncomplete: false, extinctionNone: false,
    gamma: null, alpha: null, riOil: null, lambdaPara: null, lambdaPerb: null,
    parallelColor: null, perpendicularColor: null,
  };
}

/**
 * Is this exam row still untouched?
 *
 * The PLM Exam tab opens with ONE blank row ready to type into, the way legacy
 * does (its grid shows an empty new-row placeholder).  That convenience must not
 * turn into a saved empty component, so the save drops any row the analyst never
 * filled in.
 *
 * "Untouched" is judged on the fields the analyst actually enters — Component,
 * Name, FiberColor, Result and the free-text boxes — plus every optical flag.
 * `sort` and `examOid` are bookkeeping and are ignored; a row that already has an
 * `examOid` is PERSISTED, so it is never blank by this definition (deleting one is
 * the − button's job, not the save's).
 */
export function isBlankExam(e: PlmExamInput): boolean {
  if (e.examOid) return false;
  const emptyText = (v: string | null | undefined) => v == null || v.trim() === '';
  return emptyText(e.fiberTypeOid) && emptyText(e.fiberColorOid)
    && emptyText(e.name) && emptyText(e.result)
    && emptyText(e.pleochroismColor) && emptyText(e.morphologyText)
    && emptyText(e.gamma) && emptyText(e.alpha) && emptyText(e.riOil)
    && emptyText(e.lambdaPara) && emptyText(e.lambdaPerb)
    && emptyText(e.parallelColor) && emptyText(e.perpendicularColor)
    && !e.typeLow && !e.typeMedium && !e.typeHigh && !e.typeIsotropic
    && !e.pleochroismYes && !e.pleochroismNo
    && !e.morphologyBundles && !e.morphologyCurved && !e.morphologyRibbon
    && !e.morphologySplayed && !e.morphologyStraight && !e.morphologyWavey
    && !e.morphologyUniform
    && !e.elongationPositive && !e.elongationNegative && !e.elongationBoth
    && !e.extinctionOblique && !e.extinctionParallel && !e.extinctionUndulose
    && !e.extinctionIncomplete && !e.extinctionNone;
}

/**
 * The percentage a NEW exam row starts on: 100 minus everything already
 * recorded, or blank when that leaves nothing.  Port of legacy
 * `PLMAddType_Execute` (PLMViewController.cs :1640-1672) — it sums the parsable
 * results, and a non-positive balance yields no pre-fill rather than "0".
 *
 * Unparsable results ("Trace", "<1", "") contribute nothing, exactly as
 * legacy's `decimal.TryParse` does.
 */
export function balanceResult(exams: readonly Pick<PlmExamInput, 'result'>[]): string | null {
  let total = 0;
  for (const e of exams) {
    const n = Number.parseFloat((e.result ?? '').trim());
    if (Number.isFinite(n)) total += n;
  }
  if (total <= 0) return null;
  const balance = 100 - total;
  if (balance <= 0) return null;
  // Trim float noise (100 - 28 - 71.5 → 0.5, not 0.5000000000000284).
  return String(Math.round(balance * 1e6) / 1e6);
}

/** Case-insensitive, whitespace-tolerant membership in a '/'-delimited list. */
function has(list: string | null | undefined, token: string): boolean {
  if (!list) return false;
  return list.split('/').some(p => p.trim().toLowerCase() === token.toLowerCase());
}

function eq(value: string | null | undefined, token: string): boolean {
  return (value ?? '').trim().toLowerCase() === token.toLowerCase();
}

/** Non-empty string, else undefined so the caller can keep what is there. */
function keep(value: string | null | undefined): string | undefined {
  const v = (value ?? '').trim();
  return v.length > 0 ? value! : undefined;
}

/**
 * Applies a `dbo.FiberTypesValues` row to an exam row — the port of legacy
 * `LoadPLMEDVal` (PLMViewController.cs :3292).
 *
 * **Additive, exactly like legacy**: it only ever SETS a checkbox to true and
 * only overwrites a text field when the source has a value.  It never clears
 * what the analyst already ticked, so re-picking the same name is harmless and
 * a manual override survives.
 *
 * Two faithful reproductions of legacy gaps, deliberately NOT "fixed" here —
 * changing either would silently alter results the lab has been signing off:
 *  • `Birefringence` ("Low"/"Medium"/"High") is NOT mapped to the
 *    Low/Medium/High checkboxes. Legacy reads only `Isotropic`.
 *  • `Extinction` values "Incomplete" and "None" are NOT applied — those two
 *    branches are commented out in legacy (:3358-3365).
 * Both are raised in the migration log for the lab to confirm.
 */
export function applyFiberTypeValues(exam: PlmExamInput, v: PlmFiberTypeValue): PlmExamInput {
  const next: PlmExamInput = { ...exam };

  if (eq(v.isotropic, 'yes')) next.typeIsotropic = true;

  if (keep(v.pleochroism) !== undefined) next.pleochroismColor = v.pleochroism;

  if (has(v.morphology, 'Bundles'))  next.morphologyBundles = true;
  if (has(v.morphology, 'Curved'))   next.morphologyCurved = true;
  if (has(v.morphology, 'Ribbon'))   next.morphologyRibbon = true;
  if (has(v.morphology, 'Splayed'))  next.morphologySplayed = true;
  if (has(v.morphology, 'Straight')) next.morphologyStraight = true;
  if (has(v.morphology, 'Wavey'))    next.morphologyWavey = true;
  if (has(v.morphology, 'Uniform'))  next.morphologyUniform = true;

  if (has(v.extinction, 'Oblique'))  next.extinctionOblique = true;
  if (has(v.extinction, 'Parallel')) next.extinctionParallel = true;
  if (has(v.extinction, 'Undulose')) next.extinctionUndulose = true;
  // 'Incomplete' / 'None' intentionally unmapped — see the doc comment.

  if (eq(v.elongation, 'positive'))      next.elongationPositive = true;
  else if (eq(v.elongation, 'negative')) next.elongationNegative = true;
  else if (eq(v.elongation, 'both'))     next.elongationBoth = true;

  if (keep(v.riGamma)   !== undefined) next.gamma      = v.riGamma;
  if (keep(v.riAlpha)   !== undefined) next.alpha      = v.riAlpha;
  // No λ Parallel / λ Perpendicular: the wavelength inputs are not used (owner 2026-10-20).
  if (keep(v.ri)        !== undefined) next.riOil      = v.ri;

  if (keep(v.parallelColor)      !== undefined) next.parallelColor      = v.parallelColor;
  if (keep(v.perpendicularColor) !== undefined) next.perpendicularColor = v.perpendicularColor;

  return next;
}

/** Looks a fiber name up in the autofill table, case-insensitively. */
export function findFiberTypeValue(
  values: readonly PlmFiberTypeValue[], name: string | null,
): PlmFiberTypeValue | undefined {
  const key = (name ?? '').trim().toLowerCase();
  if (!key) return undefined;
  return values.find(v => (v.name ?? '').trim().toLowerCase() === key);
}

/**
 * The component-name options for a fiber type.  Legacy switches the "Name"
 * combo's datasource on the FiberTypes value (Grid_Load :1264) — so an unknown
 * type yields NO options rather than every name, matching legacy's
 * `DataSource = null` fallback.
 */
export function namesForFiberType(
  all: readonly { fiberType: string | null; name: string | null }[],
  fiberType: string | null,
): string[] {
  const key = (fiberType ?? '').trim().toLowerCase();
  if (!key) return [];
  return all
    .filter(n => (n.fiberType ?? '').trim().toLowerCase() === key)
    .map(n => n.name ?? '')
    .filter(Boolean);
}
